import { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  LogOut,
  Home,
  Shield,
  Menu,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { AdminNavigationProvider } from "@/contexts/AdminNavigationContext";
import { AdminSidebarManager } from "@/components/admin/AdminSidebarManager";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface SidebarSnapshotItem {
  label: string;
  isVisible: boolean;
}

interface SidebarSnapshotSection {
  id: string;
  items: SidebarSnapshotItem[];
}

const AdminDashboardLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);
  const [sidebarSections, setSidebarSections] = useState<SidebarSnapshotSection[]>([]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (!session?.user) {
          navigate("/auth");
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (!session?.user) {
        navigate("/auth");
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Check user role (admin, editor, or viewer) and allowed tabs
  useEffect(() => {
    const checkAccess = async () => {
      if (!user) return;

      try {
        const { data, error } = await supabase
          .from("user_roles")
          .select("role, allowed_tabs")
          .eq("user_id", user.id)
          .in("role", ["admin", "editor", "viewer"])
          .maybeSingle();

        if (error) {
          console.error("Error checking user role:", error);
          toast({ title: "Error", description: "Failed to verify access. Please try again.", variant: "destructive" });
          return;
        }

        if (!data) {
          toast({ title: "Access denied", description: "You don't have access to this area", variant: "destructive" });
          navigate("/home");
          return;
        }
        setUserRole(data.role);
        setAllowedTabs(data.allowed_tabs || []);
        setHasAccess(true);
      } catch (err) {
        console.error("Unexpected error in access check:", err);
        toast({ title: "Error", description: "Something went wrong verifying access.", variant: "destructive" });
      }
    };

    checkAccess();
  }, [user, navigate, toast]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  // Determine active section from URL
  const getActiveSection = () => {
    const path = location.pathname;
    if (path.includes('/admin/home')) return 'home';
    if (path.includes('/admin/notifications')) return 'notifications';
    if (path.includes('/admin/education')) return 'education';
    if (path.includes('/admin/creators')) return 'creators';
    if (path.includes('/admin/video') || path.includes('/admin/duplicating')) return 'creating';
    if (path.includes('/admin/tracking') || path.includes('/admin/compass') || 
        path.includes('/admin/calendar') || path.includes('/admin/sma') || 
        path.includes('/admin/dm-automation') || path.includes('/admin/viral-content')) return 'social-media';
    if (path.includes('/admin/referrals')) return 'referrals';
    if (path.includes('/admin/invoices')) return 'invoices';
    if (path.includes('/admin/settings')) return 'settings';
    return 'creators';
  };

  const getRoleConfig = () => {
    switch (userRole) {
      case 'admin':
        return { label: 'ADMIN', color: 'red' };
      case 'editor':
        return { label: 'EDITOR', color: 'blue' };
      case 'viewer':
        return { label: 'VIEWER', color: 'gray' };
      default:
        return { label: 'USER', color: 'gray' };
    }
  };

  const activeSection = getActiveSection();
  const activeSectionTitle = useMemo(() => {
    const section = sidebarSections.find((entry) => entry.id === activeSection);
    return section?.id === "notifications" ? "Notifications" : section?.id === "home" ? "Home" : section?.id ? section.id.replace("-", " ") : "Admin";
  }, [activeSection, sidebarSections]);
  const activeSidebarLabels = useMemo(() => {
    const section = sidebarSections.find((entry) => entry.id === activeSection);
    if (!section) return [];
    return section.items.filter((item) => item.isVisible).map((item) => item.label);
  }, [activeSection, sidebarSections]);
  const roleConfig = getRoleConfig();

  if (loading || !hasAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <CreditsProvider userId={user?.id || ""}>
      <AdminNavigationProvider value={{ isAdminLayout: true, activeSidebarLabels, sharedHeaderDivider: true }}>
      <div className="relative min-h-screen h-screen bg-background flex overflow-hidden">
        <header className="md:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-background border-b border-border flex items-center justify-between px-3">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button
                className="p-2 rounded-lg hover:bg-muted min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Open admin menu"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[88vw] max-w-[340px] p-0">
              <div className="h-14 px-4 border-b border-border flex items-center">
                <h2 className="text-[15px] font-semibold text-foreground">Admin Menu</h2>
              </div>
              <nav className="p-3 overflow-y-auto h-[calc(100vh-56px)]">
                {(sidebarSections.length ? sidebarSections : []).map((section) => {
                  const visibleItems = section.items.filter((item) => item.isVisible);
                  if (!visibleItems.length) return null;
                  return (
                    <div key={section.id} className="mb-4">
                      <p className="px-2 mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {section.id.replace("-", " ")}
                      </p>
                      {visibleItems.map((item) => {
                        const active = location.pathname === item.path || location.pathname.startsWith(item.path.split("?")[0] + "/");
                        return (
                          <Link
                            key={`${section.id}-${item.path}`}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={`block px-3 py-2 rounded-lg text-sm mb-1 ${
                              active ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                            }`}
                          >
                            {item.label}
                          </Link>
                        );
                      })}
                    </div>
                  );
                })}
              </nav>
            </SheetContent>
          </Sheet>

          <h1 className="text-sm font-semibold text-foreground capitalize">{activeSectionTitle}</h1>

          <button
            className="w-10 h-10 min-h-[44px] min-w-[44px] rounded-lg bg-primary/10 flex items-center justify-center"
            onClick={() => navigate("/admin/settings")}
            aria-label="Open settings"
          >
            <Shield className="w-5 h-5 text-primary" />
          </button>
        </header>

        <div
          className="pointer-events-none absolute top-14 left-16 right-0 z-40 border-b border-border hidden md:block"
          data-testid="admin-shared-top-header-divider"
        />
        
        {/* Logo Header in Icon Sidebar */}
        <div className="fixed top-0 left-0 w-16 h-14 bg-background border-r border-b border-border hidden md:flex items-center justify-center z-50">
          <Popover open={showUserMenu} onOpenChange={setShowUserMenu}>
            <PopoverTrigger asChild>
              <button className="flex items-center justify-center hover:opacity-80 transition-opacity relative" aria-label="User menu">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  roleConfig.color === 'red' ? 'bg-red-500/20' : 
                  roleConfig.color === 'blue' ? 'bg-blue-500/20' : 'bg-gray-500/20'
                }`}>
                  <Shield className={`w-5 h-5 ${
                    roleConfig.color === 'red' ? 'text-red-500' : 
                    roleConfig.color === 'blue' ? 'text-blue-500' : 'text-gray-500'
                  }`} />
                </div>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start" side="right" sideOffset={8}>
              <div className="p-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                    roleConfig.color === 'red' ? 'bg-red-500/20 text-red-500' : 
                    roleConfig.color === 'blue' ? 'bg-blue-500/20 text-blue-500' : 'bg-gray-500/20 text-gray-500'
                  }`}>{roleConfig.label}</span>
                </div>
                <p className="text-[15px] font-semibold text-foreground mt-2">{user?.email?.split("@")[0] || "User"}</p>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
              <div className="p-2">
                <Link 
                  to="/admin/settings"
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-[15px] rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <Shield className="w-5 h-5 text-muted-foreground" />
                  Settings
                </Link>
                <Link 
                  to="/home"
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-[15px] rounded-lg hover:bg-muted transition-colors"
                  onClick={() => setShowUserMenu(false)}
                >
                  <Home className="w-5 h-5 text-muted-foreground" />
                  Client View
                </Link>
              </div>
              <div className="border-t border-border p-2">
                <button 
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-[15px] text-red-500 rounded-lg hover:bg-red-500/10 transition-colors"
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="w-5 h-5" />
                  Log out
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Sidebar Manager with edit controls */}
        <div className="hidden md:flex pt-0">
          <AdminSidebarManager 
            activeSection={activeSection}
            onSectionsChange={setSidebarSections}
            userRole={userRole}
            allowedTabs={allowedTabs}
          />
        </div>

        {/* Main Content */}
        <main
          role="main"
          className="flex-1 overflow-y-auto pt-14 md:pt-0"
        >
          <div className="h-full">
            <Outlet context={{ user, isAdmin: userRole === 'admin', userRole }} />
          </div>
        </main>
      </div>
      </AdminNavigationProvider>
    </CreditsProvider>
  );
};

export default AdminDashboardLayout;
